/**
 * RouteCardMenu — kebab-menu on a route card.
 * Exposes "Openen in planner", "Verplaatsen naar andere dag…", "Samenvoegen
 * met…" and "Route annuleren…". Disables merge and reschedule when the route
 * is locked; reschedule and cancel only for routes that are not yet underway
 * or done.
 */

import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import type { RouteListItem } from '../../lib/salesforce';

interface RouteCardMenuProps {
  route: RouteListItem;
  onOpenInPlanner: () => void;
  onMergeClick: () => void;
  /** Chauffeur toewijzen of wisselen (ook op een gelockte route: ziekte op de dag zelf). */
  onAssignChauffeur?: () => void;
  /** Route annuleren (verb cancel); verborgen op Onderweg/Afgerond/Geannuleerd. */
  onCancelClick?: () => void;
  /** Hele route naar een andere dag (POST /routes/{id}/reschedule); verborgen op Onderweg/Afgerond/Geannuleerd. */
  onRescheduleClick?: () => void;
  compact?: boolean;
}

const AFGEHANDELDE_STATUSSEN = ['Onderweg', 'Afgerond', 'Geannuleerd'];

/**
 * Welke acties het menu toont en of ze klikbaar zijn. Gedeeld met de test,
 * zodat de keuze (niet alleen de knop) bewaakt is: verplaatsen en annuleren
 * verdwijnen zodra de route rijdt of klaar is; verplaatsen en samenvoegen
 * blokkeren op een gelockte route.
 */
export function routeMenuStand(route: Pick<RouteListItem, 'status' | 'locked'>) {
  const isAfgehandeld = AFGEHANDELDE_STATUSSEN.includes(route.status ?? '');
  return {
    isAfgehandeld,
    toonVerplaatsen: !isAfgehandeld,
    toonAnnuleren: !isAfgehandeld,
    verplaatsenGeblokkeerd: route.locked,
    samenvoegenGeblokkeerd: route.locked,
  };
}

export function RouteCardMenu({
  route,
  onOpenInPlanner,
  onMergeClick,
  onAssignChauffeur,
  onCancelClick,
  onRescheduleClick,
  compact = false,
}: RouteCardMenuProps) {
  const stand = routeMenuStand(route);
  const kanAnnuleren = !!onCancelClick && stand.toonAnnuleren;
  const kanVerplaatsen = !!onRescheduleClick && stand.toonVerplaatsen;
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;

    const handleDocumentClick = (event: MouseEvent) => {
      if (!containerRef.current) return;
      if (containerRef.current.contains(event.target as Node)) return;
      setOpen(false);
    };

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const handleToggle = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setOpen((prev) => !prev);
  };

  const handleItem = (event: React.MouseEvent, action: () => void) => {
    event.stopPropagation();
    event.preventDefault();
    setOpen(false);
    action();
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={handleToggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Route-acties"
        className={clsx(
          'flex items-center justify-center rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100',
          'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1',
          compact ? 'w-5 h-5' : 'w-6 h-6'
        )}
      >
        <svg viewBox="0 0 20 20" fill="currentColor" className={compact ? 'w-3 h-3' : 'w-4 h-4'}>
          <path d="M10 6a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm0 5.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm0 5.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 mt-1 w-52 rounded-md border border-gray-200 bg-white shadow-lg z-20 py-1 text-sm"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            role="menuitem"
            onClick={(e) => handleItem(e, onOpenInPlanner)}
            className="w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-50"
          >
            Openen in planner
          </button>
          {onAssignChauffeur && (
            <button
              type="button"
              role="menuitem"
              onClick={(e) => handleItem(e, onAssignChauffeur)}
              className="w-full text-left px-3 py-2 text-gray-700 hover:bg-gray-50"
            >
              {route.chauffeurName ? 'Chauffeur wijzigen…' : 'Chauffeur toewijzen…'}
            </button>
          )}
          {kanVerplaatsen && (
            <button
              type="button"
              role="menuitem"
              onClick={(e) => handleItem(e, onRescheduleClick!)}
              disabled={route.locked}
              title={route.locked ? 'Vergrendelde route kan niet worden verplaatst. Ontgrendel hem eerst in de planner' : undefined}
              className={clsx(
                'w-full text-left px-3 py-2',
                route.locked
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-50'
              )}
            >
              Verplaatsen naar andere dag…
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={(e) => handleItem(e, onMergeClick)}
            disabled={route.locked}
            title={route.locked ? 'Vergrendelde route kan niet worden samengevoegd' : undefined}
            className={clsx(
              'w-full text-left px-3 py-2',
              route.locked
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-50'
            )}
          >
            Samenvoegen met…
          </button>
          {kanAnnuleren && (
            <button
              type="button"
              role="menuitem"
              onClick={(e) => handleItem(e, onCancelClick!)}
              className="w-full text-left px-3 py-2 text-red-700 hover:bg-red-50 border-t border-gray-100 mt-1"
            >
              Route annuleren…
            </button>
          )}
        </div>
      )}
    </div>
  );
}
